import fetchService from './fetchService';
import { createFileLogger } from '../utils/logger';
import { DEFAULT_CLAUDE_MODEL, DEFAULT_EXPERT_MODEL, AVAILABLE_CLAUDE_MODELS } from '../config/claude';
import { API_BASE_URL, API_ORIGIN_URL } from '../api/config';
import { userService } from './users/userService';

const log = createFileLogger('MODELSERVICE_SERVICE', {enabled: false});

export interface Model {
  id: string;
  name: string;
  source: 'cloud' | 'galbox' | 'personal';
  available: boolean;
}

class ModelService {
  private models: Model[] = [];
  private isLoading = false;
  private loadingPromise: Promise<Model[]> | null = null;
  private listeners: Set<(models: Model[]) => void> = new Set();
  private hasInitialLoad = false; // Track if we've attempted initial load
  
  // Circuit breaker to prevent repeated failed requests
  private circuitBreaker = {
    failures: 0,
    lastFailureTime: 0,
    isOpen: false,
    openDuration: 60000, // 1 minute
    maxFailures: 3
  };
  
  // Check if we're using Galbox Online (gs.*.riversystem.ai or api.riversystem.ai)
  isGalboxOnline(): boolean {
    if (!API_ORIGIN_URL) return false;
    
    try {
      const url = new URL(API_ORIGIN_URL);
      const hostname = url.hostname.toLowerCase();
      
      // Check if it's a gs.*.riversystem.ai or api.riversystem.ai endpoint
      return (hostname.startsWith('gs.') && hostname.includes('riversystem.ai')) ||
             hostname === 'api.riversystem.ai' ||
             hostname.startsWith('api.') && hostname.includes('riversystem.ai');
    } catch {
      return false;
    }
  }
  
  // Get the Galbox Online hostname for display
  getGalboxOnlineHost(): string | null {
    if (!this.isGalboxOnline()) return null;
    
    try {
      const url = new URL(API_ORIGIN_URL);
      return url.hostname;
    } catch {
      return null;
    }
  }

  async downloadModel(modelName: string, galBoxServerStatus?: { online: boolean; address?: string }, onProgress?: (status: any) => void): Promise<void> {
    // Check if we're in Galbox Online mode
    if (this.isGalboxOnline()) {
      // Use the Galbox Online endpoint for downloading models
      const url = `${API_ORIGIN_URL}/download-model`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelName })
      });
      if (!response.ok || !response.body) {
        const text = await response.text().catch(() => '');
        throw new Error(text || `Failed to start download: ${response.status}`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split('\n')) {
          if (!line.trim()) continue;
          if (onProgress) onProgress(line);
        }
      }
    } else if (galBoxServerStatus?.online && galBoxServerStatus.address) {
      // Use local Galbox server
      const url = `http://${galBoxServerStatus.address}:3001/download-model`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelName })
      });
      if (!response.ok || !response.body) {
        const text = await response.text().catch(() => '');
        throw new Error(text || `Failed to start download: ${response.status}`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split('\n')) {
          if (!line.trim()) continue;
          if (onProgress) onProgress(line);
        }
      }
    } else {
      throw new Error('No GalBox server available for downloading models');
    }
  }

  async loadModels(galBoxServerStatus?: { online: boolean; address?: string }): Promise<Model[]> {
    // Always return immediately available models for fast UI response
    if (this.models.length > 0 && !this.isLoading) {
      return this.models;
    }

    // If already loading, return the promise
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // If no models yet, immediately provide fallback models for instant UI response
    if (this.models.length === 0 && !this.hasInitialLoad) {
      const fallbackModels: Model[] = [];
      this._loadFallbackClaudeModels(fallbackModels);
      this.models = fallbackModels;
      this.notifyListeners(fallbackModels);
      log.log('[ModelService] Provided immediate fallback models for fast UI');
    }

    this.isLoading = true;
    this.hasInitialLoad = true;
    this.loadingPromise = this._loadModelsInternal(galBoxServerStatus);

    try {
      const models = await this.loadingPromise;
      this.models = models;
      this.notifyListeners(models);
      return models;
    } catch (error) {
      log.error('[ModelService] Failed to load models:', error);
      this.loadingPromise = null;
      // Keep fallback models on error
      if (this.models.length === 0) {
        const fallbackModels: Model[] = [];
        this._loadFallbackClaudeModels(fallbackModels);
        this.models = fallbackModels;
        this.notifyListeners(fallbackModels);
      }
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  private async _loadModelsInternal(galBoxServerStatus?: { online: boolean; address?: string }): Promise<Model[]> {
    const models: Model[] = [];

    // Check if we're using Galbox through tunnel (private mode)
    const isUsingTunnel = this.isGalboxOnline();
    
    // Check circuit breaker before making cloud model requests
    const now = Date.now();
    if (this.circuitBreaker.isOpen) {
      if (now - this.circuitBreaker.lastFailureTime > this.circuitBreaker.openDuration) {
        // Reset circuit breaker after cooldown
        this.circuitBreaker.isOpen = false;
        this.circuitBreaker.failures = 0;
        log.log('[ModelService] Circuit breaker reset after cooldown');
      } else {
        log.warn('[ModelService] Circuit breaker is open, skipping cloud model fetch');
        this._loadFallbackClaudeModels(models);
        return models;
      }
    }
    
    // Always load cloud models first
    try {
      log.log('[ModelService] Fetching models from:', `${API_BASE_URL}/claude/models`);
      const result = await fetchService(`${API_BASE_URL}/claude/models`); // fetchService already returns parsed JSON
      log.log('[ModelService] Backend response:', result);
      
      // Reset circuit breaker on success
      this.circuitBreaker.failures = 0;
      this.circuitBreaker.isOpen = false;
      
      if (result.data && Array.isArray(result.data) && result.data.length > 0) {
        const cloudModels = result.data.map((model: any) => ({
          id: model.id,
          name: model.name || model.id,
          source: 'cloud' as const,
          available: true, 
        }));
        models.push(...cloudModels);
        log.log('[ModelService] Cloud models loaded from backend:', cloudModels);
        log.log(`[ModelService] Loaded ${cloudModels.length} cloud models from backend`);
        
        // Also add Venice models
        const { AVAILABLE_VENICE_MODELS } = require('../config/venice');
        const veniceModels = AVAILABLE_VENICE_MODELS.map((model: any) => ({
          id: model.id,
          name: model.name,
          source: 'cloud' as const,
          available: true,
        }));
        models.push(...veniceModels);
        log.log(`[ModelService] Added ${veniceModels.length} Venice models to cloud models`);
      } else {
        console.warn('[ModelService] No valid data from backend, using fallback models');
        this._loadFallbackClaudeModels(models);
      }
    } catch (error) {
      console.error('[ModelService] Failed to load cloud models from backend, using fallback:', error);
      log.warn('[ModelService] Failed to load cloud models from backend, using fallback:', error);
      
      // Update circuit breaker on failure
      this.circuitBreaker.failures++;
      this.circuitBreaker.lastFailureTime = Date.now();
      
      if (this.circuitBreaker.failures >= this.circuitBreaker.maxFailures) {
        this.circuitBreaker.isOpen = true;
        log.warn(`[ModelService] Circuit breaker opened after ${this.circuitBreaker.failures} failures`);
      }
      
      this._loadFallbackClaudeModels(models);
    }
    
    // If using tunnel (private mode), fetch galbox models from the gs.* URL
    if (isUsingTunnel) {
      try {
        // Convert api.riversystem.ai to gs.riversystem.ai for galbox models
        let galboxUrl = API_ORIGIN_URL;
        if (API_ORIGIN_URL.includes('api.riversystem.ai')) {
          galboxUrl = API_ORIGIN_URL.replace('api.', 'gs.');
        }
        
        const modelsEndpoint = `${galboxUrl}/models`;
        log.log('[ModelService] Fetching Galbox models from:', modelsEndpoint);
        
        const response = await fetch(modelsEndpoint);
        if (response.ok) {
          const data = await response.json();
          log.log('[ModelService] Galbox models response:', data);
          
          if (data && Array.isArray(data.models)) {
            const galboxModels = data.models.map((model: { name: string, model: string }) => ({
              id: `ollama/${model.name || model.model}`,
              name: model.name || model.model,
              source: 'galbox' as const,
              available: true,
            }));
            models.push(...galboxModels);
            log.log('[ModelService] Galbox models loaded:', galboxModels);
            log.log(`[ModelService] Loaded ${galboxModels.length} models from Galbox via gs.* endpoint`);
          }
        } else {
          console.warn('[ModelService] Failed to fetch galbox models, status:', response.status);
        }
      } catch (error) {
        console.error('[ModelService] Failed to load Galbox models from tunnel:', error);
        log.warn('[ModelService] Failed to load Galbox models from tunnel:', error);
      }
    }

    // Load GalBox models if server is online (local galbox, not online)
    if (galBoxServerStatus?.online && galBoxServerStatus.address) {
      try {
        const response = await fetch(`http://${galBoxServerStatus.address}:3001/models`);
        if (response.ok) {
          const data = await response.json();
          if (data && Array.isArray(data.models)) {
            const galBoxModels = data.models.map((model: { name: string }) => ({
              id: model.name,
              name: model.name,
              source: 'galbox' as const,
              available: true
            }));
            models.push(...galBoxModels);
            log.log(`[ModelService] Loaded ${galBoxModels.length} GalBox models`);
          }
        }
      } catch (error) {
        log.warn('[ModelService] Failed to load GalBox models:', error);
      }
    }

    // Load personal models - DISABLED (PersonalModelApp removed)
    // try {
    //   const personalModels = await personalModelService.fetchModels();
    //   const trainedModels = personalModels
    //     .filter(model => model.status === 'ready' || model.status === 'draft')
    //     .map(model => ({
    //       id: model.id,
    //       name: model.name,
    //       source: 'personal' as const,
    //       available: true
    //     }));
    //   models.push(...trainedModels);
    //   log.log(`[ModelService] Loaded ${trainedModels.length} personal models`);
    // } catch (error) {
    //   log.warn('[ModelService] Failed to load personal models:', error);
    // }

    log.log(`[ModelService] Total models loaded: ${models.length}`);
    return models;
  }

  isModelAvailable(modelId: string): boolean {
    const model = this.models.find(m => m.id === modelId);
    return model?.available ?? false;
  }

  async loadModelsWithRetry(galBoxServerStatus?: { online: boolean; address?: string }, maxRetries: number = 1): Promise<Model[]> {
    // Simplified retry - fetchService already handles retries, so we just need one attempt here
    // with fast fallback to avoid double retry delays
    try {
      log.log(`[ModelService] Loading models (fetchService handles retries)`);
      return await this.loadModels(galBoxServerStatus);
    } catch (error) {
      log.warn(`[ModelService] Failed to load models, using fallback:`, error);
      
      // Fast fallback: return fallback models immediately instead of retrying
      const fallbackModels: Model[] = [];
      this._loadFallbackClaudeModels(fallbackModels);
      this.models = fallbackModels;
      this.notifyListeners(fallbackModels);
      return fallbackModels;
    }
  }

  async reloadModels(galBoxServerStatus?: { online: boolean; address?: string }): Promise<Model[]> {
    this.models = [];
    this.loadingPromise = null;
    this.isLoading = false;
    return this.loadModels(galBoxServerStatus);
  }

  private _loadFallbackClaudeModels(models: Model[]): void {
    log.log('[ModelService] Loading fallback Claude models');
    const fallbackModels = AVAILABLE_CLAUDE_MODELS.map(model => ({
      id: model.id,
      name: model.name,
      source: 'cloud' as const,
      available: true,
    }));
    models.push(...fallbackModels);
    log.log(`[ModelService] Loaded ${fallbackModels.length} fallback Claude models`);
    
    // Also add Venice models
    const { AVAILABLE_VENICE_MODELS } = require('../config/venice');
    const veniceModels = AVAILABLE_VENICE_MODELS.map((model: any) => ({
      id: model.id,
      name: model.name,
      source: 'cloud' as const,
      available: true,
    }));
    models.push(...veniceModels);
    log.log(`[ModelService] Added ${veniceModels.length} Venice models`);
    
    // Also add Grok models
    const { AVAILABLE_GROK_MODELS } = require('../config/grok');
    const grokModels = AVAILABLE_GROK_MODELS.map((model: any) => ({
      id: model.id,
      name: model.name,
      source: 'cloud' as const,
      available: true,
    }));
    models.push(...grokModels);
    log.log(`[ModelService] Added ${grokModels.length} Grok models`);
  }

  getDefaultModel(): string {
    return DEFAULT_CLAUDE_MODEL;
  }

  /**
   * Returns the plan-aware default model.
   * Paid and PAYG users default to Expert (Sonnet); free users default to Fastest (Haiku).
   */
  getPlanAwareDefaultModel(): string {
    try {
      const useUserStore = require('../stores/userStore').default;
      const plan = useUserStore.getState().plan?.plan ?? 'free';
      const isPaid = plan !== 'free';
      return isPaid ? DEFAULT_EXPERT_MODEL : DEFAULT_CLAUDE_MODEL;
    } catch {
      return DEFAULT_CLAUDE_MODEL;
    }
  }

  async getUserPreferredModel(): Promise<string> {
    try {
      // First try to get user's stored preference
      const userDefaultModel = await userService.getDefaultModel();
      log.log('[ModelService] getUserPreferredModel - User default model from DB:', userDefaultModel);

      if (userDefaultModel) {
        // Verify the model is available
        const models = await this.loadModels();
        log.log('[ModelService] getUserPreferredModel - Available models:', models.map(m => m.id));

        const isAvailable = models.some(m => m.id === userDefaultModel && m.available);
        log.log('[ModelService] getUserPreferredModel - Is user model available?', isAvailable);

        if (isAvailable) {
          log.log(`[ModelService] Using user's preferred model: ${userDefaultModel}`);
          return userDefaultModel;
        }
        // Venice models are allowed even if not in list yet
        if (userDefaultModel.startsWith('venice/')) {
          log.log('[ModelService] Allowing Venice model even though not in list yet:', userDefaultModel);
          return userDefaultModel;
        }
        log.warn(`[ModelService] User preferred model ${userDefaultModel} unavailable, using plan-aware default`);
      }

      // Fall back to plan-aware default: Expert for paid/PAYG, Fastest for free
      const planDefault = this.getPlanAwareDefaultModel();
      log.log(`[ModelService] No valid user preference, using plan-aware default: ${planDefault}`);
      return planDefault;
    } catch (error) {
      log.error('[ModelService] Error getting user preferred model, using plan-aware default:', error);
      return this.getPlanAwareDefaultModel();
    }
  }

  isReady(): boolean {
    return this.models.length > 0 && !this.isLoading;
  }

  isLoadingModels(): boolean {
    return this.isLoading;
  }

  hasModels(): boolean {
    return this.models.length > 0;
  }

  getModels(): Model[] {
    return this.models;
  }

  subscribe(callback: (models: Model[]) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(models: Model[]): void {
    this.listeners.forEach(callback => callback(models));
  }
}

export const modelService = new ModelService();
